import { z } from 'zod';

/**
 * Общие примитивы контракта коннектора.
 *
 * Наружу — в проект-потребитель и обратно — идёт snake_case: контракт реализуют
 * на PHP (new-fomos) и Python, где camelCase выглядит чужеродно. Внутренние DTO
 * платформы (src/dto) остаются camelCase. Граница между двумя нотациями
 * проходит ровно по этому каталогу и нигде больше.
 *
 * Все схемы здесь строгие: неизвестные ключи z.object отбрасывает, значения
 * проверяются по типу. Мы рисуем чужие данные в панели оператора, поэтому
 * «примерно похожий» ответ проекта должен падать на границе, а не растекаться
 * по фронту в виде undefined.
 */

/** Тональность подсветки: badge, timeline, события ingest. */
export const TONES = ['success', 'warning', 'danger', 'neutral'] as const;
export const toneSchema = z.enum(TONES);
export type Tone = z.infer<typeof toneSchema>;

/**
 * Тональность кнопки действия — это про стиль кнопки, а не про статус данных,
 * поэтому набор другой: нужен primary (главное действие) и не нужен success.
 */
export const ACTION_TONES = ['primary', 'neutral', 'warning', 'danger'] as const;
export const actionToneSchema = z.enum(ACTION_TONES);
export type ActionTone = z.infer<typeof actionToneSchema>;

// Потолки длин. Проект не должен иметь возможности положить панель оператора
// строкой на мегабайт; всё, что длиннее, — ошибка интеграции, а не контент.
export const CONNECTOR_MAX_KEY_LENGTH = 64;
export const CONNECTOR_MAX_LABEL_LENGTH = 120;
export const CONNECTOR_MAX_VALUE_LENGTH = 2000;
export const CONNECTOR_MAX_URL_LENGTH = 2048;
export const CONNECTOR_MAX_LIST_ITEMS = 50;
export const CONNECTOR_MAX_GROUPS = 20;
export const CONNECTOR_MAX_FIELDS_PER_GROUP = 40;
export const CONNECTOR_MAX_ACTIONS = 20;
export const CONNECTOR_MAX_ACTION_PARAMS = 10;
export const CONNECTOR_MAX_SELECT_OPTIONS = 100;
export const CONNECTOR_MAX_TIMELINE_ITEMS = 50;

/** Машинный идентификатор поля/действия: по нему проект узнаёт, что нажали. */
export const connectorKeySchema = z
  .string()
  .min(1)
  .max(CONNECTOR_MAX_KEY_LENGTH)
  .regex(/^[a-zA-Z0-9_.:-]+$/, 'key: допустимы A-Za-z0-9 и _.:-');

/** Человекочитаемая подпись, её видит оператор. */
export const connectorLabelSchema = z.string().min(1).max(CONNECTOR_MAX_LABEL_LENGTH);

export const connectorTextSchema = z.string().max(CONNECTOR_MAX_VALUE_LENGTH);

/**
 * Ссылка наружу. Схема ограничена http/https: значение попадает в href в панели
 * оператора, и javascript:/data: там равносильны XSS.
 */
export const connectorUrlSchema = z
  .url({ protocol: /^https?$/ })
  .max(CONNECTOR_MAX_URL_LENGTH);

/**
 * ISO 8601 с обязательным смещением (`Z` или `+03:00`).
 * Время без зоны неоднозначно, а мы его показываем оператору в его часовом
 * поясе — «просто дата-время» здесь означает молча неверную дату.
 */
export const connectorDateTimeSchema = z.iso.datetime({ offset: true });

/**
 * telegram_id. В Telegram это глобальный id пользователя, одинаковый во всех
 * ботах, — именно на нём держится привязка личности.
 *
 * coerce нужен потому, что в query-строке (GET контекста) он всегда строка,
 * а в JSON-теле ingest — число. Ограничение MAX_SAFE_INTEGER не декоративное:
 * Telegram обещает влезать в 52 бита, и всё, что больше, — уже потеря точности
 * при разборе JSON, а не валидный id.
 */
export const telegramIdSchema = z.coerce
  .number()
  .int()
  .positive()
  .max(Number.MAX_SAFE_INTEGER);

/** id человека в системе проекта. Формат наш не волнует — это чужой ключ. */
export const externalIdSchema = z.string().min(1).max(128);

/** Скалярное JSON-значение: то, что можно положить в параметр действия. */
export const jsonScalarSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
export type JsonScalar = z.infer<typeof jsonScalarSchema>;

/**
 * Произвольное JSON-значение — рекурсивно, а не `unknown`.
 *
 * Тип обязан описывать именно JSON: `data` события и `meta` резолва уходят
 * в Json-колонку Prisma и в payload WS. `Record<string, unknown>` в эту
 * колонку не ложится (Prisma.InputJsonValue требует значений, а не unknown),
 * а главное — пропускает Date, BigInt и функции, то есть ровно те значения,
 * на которых JSON.stringify либо бросает, либо молча теряет поле.
 */
export type JsonValue = JsonScalar | JsonValue[] | { [key: string]: JsonValue };
export interface JsonObject {
  [key: string]: JsonValue;
}

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([jsonScalarSchema, z.array(jsonValueSchema), z.record(z.string(), jsonValueSchema)]),
);

/** Произвольный JSON-объект (data события, meta резолва). */
export const jsonObjectSchema: z.ZodType<JsonObject> = z.lazy(() =>
  z.record(z.string(), jsonValueSchema),
);
